import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { Col, Input, Modal, Radio, Row, Space, Spin, Table } from "antd";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import PFButton from "../../../components/functional_components/Button/PFButton";
import { setLoading } from "../../../redux/ducks/Loading";
import ProductCategory from "../../../services/api/ProductCategory";
import { paginationDefaults, productCategoryTypes } from "../../../utils/properties";
import { showError, showSuccess, trimData } from "../../../utils/utilities";

const { confirm } = Modal;

const ProductCategories = () => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    tableContent: [],
    selectedProductCategory: {},
    loading: false,
    modalVisible: {
      visible: false,
      type: "",
    },
    currentPage: paginationDefaults.page,
    pageSize: paginationDefaults.size,
    totalItems: paginationDefaults.totalItems,
  });
  const dispatch = useDispatch();

  const columns = [
    { title: t("productCategories.table.id"), dataIndex: "id", key: "id" },
    { title: t("productCategories.table.name"), dataIndex: "name", key: "name", responsive: ["md"] },
    {
      title: t("productCategories.table.type"),
      dataIndex: "type",
      key: "type",
      render: (type) => {
        return type ? t(`productCategories.${type.toLowerCase()}`) : null;
      },
    },
    {
      title: t("general.actions"),
      dataIndex: "",
      key: "x",
      width: 80,
      render: (record) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(record.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(record.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const formikEditProductCategory = useFormik({
    // id, name and type refers to product category ID, description and type
    initialValues: {
      id: get(state.selectedProductCategory, "id", ""),
      name: get(state.selectedProductCategory, "name", ""),
      type: get(state.selectedProductCategory, "type", ""),
      contoCoGe: get(state.selectedProductCategory, "contoCoGe", ""),
      contoCoGeBPC: get(state.selectedProductCategory, "contoCoGeBPC", ""),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.id) {
        errors.id = "Required!";
      }
      if (!values.name) {
        errors.name = "Required!";
      }
      if (!values.type) {
        errors.type = "Required!";
      }
      if (!values.contoCoGe) {
        errors.contoCoGe = "Required!";
      }
      if (!values.contoCoGeBPC) {
        errors.contoCoGeBPC = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const saveResponse = await ProductCategory.saveProductCategory(values);
      if (!saveResponse.error) {
        showSuccess();
        getProdCategories();
        closeModal();
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  useEffect(() => {
    getProdCategories();
  }, []);

  const getProdCategories = async (page = state.currentPage) => {
    dispatch(setLoading(true));
    const productCategories = await ProductCategory.productCategories(`page=${page - 1}&size=${state.pageSize}`);
    if (!productCategories.error && productCategories.data) {
      productCategories.data.forEach((data, idx) => {
        data.key = idx;
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: productCategories.data,
        totalItems: productCategories.totalItems,
      }));
    } else {
      showError(get(productCategories, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const editCallback = async (selectedValue) => {
    setState((prevState) => ({ ...prevState, loading: true, modalVisible: { visible: true, type: "edit" } }));
    const productCategoryDetail = await ProductCategory.productDetail(selectedValue);
    if (!productCategoryDetail.error && productCategoryDetail.data) {
      setState((prevState) => ({ ...prevState, selectedProductCategory: productCategoryDetail.data }));
    } else {
      showError(get(productCategoryDetail, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (selectedValue) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await ProductCategory.deleteProductCategory(selectedValue);
        if (!deleteResponse.error) {
          showSuccess();
          getProdCategories();
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const closeModal = () => {
    if (state.modalVisible.type === "new") {
      formikEditProductCategory.resetForm();
    }
    setState((prevState) => ({ ...prevState, selectedProductCategory: {} }));
    setState((prevState) => ({ ...prevState, modalVisible: { visible: false, type: state.modalVisible.type } }));
  };

  return (
    <>
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <PFButton
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() =>
            setState((prevState) => ({ ...prevState, modalVisible: { visible: true, type: "new" } }))
          }
        />
      </Row>
      <Table
        pagination={{
          current: state.currentPage,
          pageSize: state.pageSize,
          total: state.totalItems,
          onChange: (page) => {
            page -= 1;
            setState((prevState) => ({ ...prevState, currentPage: page }));
            getProdCategories(page);
          },
        }}
        columns={columns}
        dataSource={state.tableContent}
        size="small"
      />
      <Modal
        width={700}
        title={state.modalVisible.type === "edit" ? state.selectedProductCategory.id : "Nuova categoria prodotto"}
        open={state.modalVisible.visible}
        onCancel={closeModal}
        footer={[
          <PFButton
            key="okButton"
            isDisabled={!(formikEditProductCategory.dirty && formikEditProductCategory.isValid)}
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikEditProductCategory.submitForm}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={isEmpty(state.selectedProductCategory)} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col lg={9} md={9} xs={24} sm={9}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="id">
                {t("productCategories.table.id")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="id"
                name="id"
                onChange={formikEditProductCategory.handleChange}
                onBlur={formikEditProductCategory.handleBlur}
                value={formikEditProductCategory.values.id}
                disabled={state.modalVisible.type === "edit" ? true : false}
              />
            </Row>
          </Col>
          <Col lg={9} md={9} xs={24} sm={9}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="name">
                {t("productCategories.table.name")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="name"
                name="name"
                onChange={formikEditProductCategory.handleChange}
                onBlur={formikEditProductCategory.handleBlur}
                value={formikEditProductCategory.values.name}
              />
            </Row>
          </Col>
          <Col lg={6} md={6} xs={24} sm={4}>
            <Row>
              <label className="fw-bold mb-10 mt-10" htmlFor="type">
                {t("productCategories.table.type")}*
              </label>
            </Row>
            <Row>
              <Radio.Group
                id="type"
                name="type"
                onChange={formikEditProductCategory.handleChange("type")}
                value={formikEditProductCategory.values.type}
              >
                <Space direction="vertical">
                  {productCategoryTypes.map((type) => {
                    return (
                      <Radio key={type.value} value={type.value}>
                        {t(`productCategories.${type.label}`)}
                      </Radio>
                    );
                  })}
                </Space>
              </Radio.Group>
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={9} md={9} xs={24} sm={9}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="contoCoGe">
                {t("productCategories.table.contoCoGe")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="contoCoGe"
                name="contoCoGe"
                onChange={formikEditProductCategory.handleChange}
                onBlur={formikEditProductCategory.handleBlur}
                value={formikEditProductCategory.values.contoCoGe}
              />
            </Row>
          </Col>
          <Col lg={9} md={9} xs={24} sm={9}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="contoCoGeBPC">
                {t("productCategories.table.contoCoGeBPC")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="contoCoGeBPC"
                name="contoCoGeBPC"
                onChange={formikEditProductCategory.handleChange}
                onBlur={formikEditProductCategory.handleBlur}
                value={formikEditProductCategory.values.contoCoGeBPC}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ProductCategories;
